/**
 * GPU spark/plume particle shaders for thruster exhaust.
 * Runs alongside the continuous flame shader (thrusterShaders.ts) to add
 * dynamic sparks and plasma plumes that shoot out and decelerate.
 *
 * All simulation is analytical in the vertex shader — no CPU per-frame updates.
 * Particles recycle via modulo on age. Two types derived from seed:
 * - Sparks (configurable %): small, fast, bright, short-lived
 * - Plumes (remainder %): larger, slower, softer, longer-lived
 *
 * Geometry: THREE.Points with aBirthTime + aSeed buffer attributes.
 *
 * All visual constants are driven by uniforms so the Particle Editor
 * can tweak them in real-time. Default values match DEFAULT_THRUSTER_SPARKS.
 */

/** Number of particles per thruster instance (default, can be overridden by config). */
export const SPARK_COUNT = 48;

// ---- Vertex Shader ----

export const sparkVertexShader = /* glsl */ `
attribute float aBirthTime;
attribute float aSeed;

uniform float uTime;
uniform float uIntensity;
uniform float uAfterburner;

// Configurable uniforms (driven by Particle Editor / particleEffectStore)
uniform float uPlumeSeedThreshold;
uniform float uSparkLifetime;
uniform float uPlumeLifetime;
uniform float uSparkSpeed;
uniform float uPlumeSpeed;
uniform float uAfterburnerSpeedBoost;
uniform float uConeAngle;
uniform float uNozzleHalfWidth;
uniform float uSparkDrag;
uniform float uPlumeDrag;
uniform float uSparkSize;
uniform float uPlumeSize;

varying float vAge;
varying float vNormAge;
varying float vSeed;
varying float vIsPlume;
varying float vAfterburner;

void main() {
  // Particle type from seed
  vIsPlume = step(uPlumeSeedThreshold, aSeed);
  vSeed = aSeed;
  vAfterburner = uAfterburner;

  // Lifetime
  float lifetime = mix(uSparkLifetime, uPlumeLifetime, vIsPlume);

  // Age with modulo recycling
  vAge = mod(uTime - aBirthTime, lifetime);
  vNormAge = vAge / lifetime;

  // Initial speed
  float baseSpeed = mix(uSparkSpeed, uPlumeSpeed, vIsPlume);
  float speed = baseSpeed * (1.0 + uAfterburnerSpeedBoost * uAfterburner);

  // Decorrelate direction from type classification.
  // aSeed is used for type (step(threshold, aSeed)) so seeds below threshold
  // are always sparks and above are plumes — using aSeed directly for angle
  // biases sparks to one side and plumes to the other.
  // Hash the seed to get an independent uniform distribution for direction.
  float dirSeed = fract(aSeed * 7.1381);

  // Cone spread from hashed seed
  float angle = (dirSeed - 0.5) * uConeAngle;
  float spreadX = sin(angle);
  float spreadZ = cos(angle);

  // Narrow nozzle lateral offset
  float lateralOffset = (dirSeed - 0.5) * 2.0 * uNozzleHalfWidth;

  // Drag deceleration
  float drag = mix(uSparkDrag, uPlumeDrag, vIsPlume);

  // Analytical position: distance = speed/drag * (1 - exp(-drag * age))
  float dist = speed / drag * (1.0 - exp(-drag * vAge));

  // Local position: exhaust extends along +Z from nozzle
  vec3 localPos = vec3(
    lateralOffset + spreadX * dist,
    0.0,
    spreadZ * dist
  );

  vec4 mvPosition = modelViewMatrix * vec4(localPos, 1.0);

  // Point size
  float baseSize = mix(uSparkSize, uPlumeSize, vIsPlume);
  // Shrink with age squared
  float ageShrink = 1.0 - vNormAge * vNormAge;
  // Perspective scaling
  float perspective = 50.0 / -mvPosition.z;

  gl_PointSize = baseSize * ageShrink * perspective * uIntensity;
  gl_Position = projectionMatrix * mvPosition;
}
`;

// ---- Fragment Shader ----

export const sparkFragmentShader = /* glsl */ `
varying float vAge;
varying float vNormAge;
varying float vSeed;
varying float vIsPlume;
varying float vAfterburner;

// Configurable uniforms
uniform float uSparkEdgeSoftness;
uniform float uPlumeEdgeSoftness;
uniform vec3 uNormalHot;
uniform vec3 uNormalMid;
uniform vec3 uNormalCool;
uniform vec3 uAbHot;
uniform vec3 uAbMid;
uniform vec3 uAbCool;
uniform float uFlickerFreq;
uniform float uFlickerAmp;
uniform float uHdrMultNormal;
uniform float uHdrMultAB;

void main() {
  // Circle shape via gl_PointCoord
  vec2 coord = gl_PointCoord - 0.5;
  float r = length(coord) * 2.0;

  // Hard-edge sparks, soft plumes
  float edgeSoftness = mix(uSparkEdgeSoftness, uPlumeEdgeSoftness, vIsPlume);
  float circle = 1.0 - smoothstep(1.0 - edgeSoftness, 1.0, r);

  // Discard outside circle
  if (circle < 0.01) discard;

  // Normal palette
  vec3 normalColor = mix(uNormalHot, uNormalMid, smoothstep(0.0, 0.4, vNormAge));
  normalColor = mix(normalColor, uNormalCool, smoothstep(0.4, 0.9, vNormAge));

  // Afterburner palette
  vec3 abColor = mix(uAbHot, uAbMid, smoothstep(0.0, 0.4, vNormAge));
  abColor = mix(abColor, uAbCool, smoothstep(0.4, 0.9, vNormAge));

  // Blend palettes
  vec3 color = mix(normalColor, abColor, vAfterburner);

  // Per-particle flicker from seed
  float flicker = (1.0 - uFlickerAmp) + uFlickerAmp * sin(vAge * uFlickerFreq + vSeed * 100.0);

  // Quadratic alpha fade with age
  float alphaFade = (1.0 - vNormAge) * (1.0 - vNormAge);

  // HDR multiplier for bloom pickup
  float hdr = mix(uHdrMultNormal, uHdrMultAB, vAfterburner);

  float alpha = circle * alphaFade * flicker;

  // Discard near-transparent
  if (alpha < 0.01) discard;

  gl_FragColor = vec4(color * hdr * flicker, alpha);
}
`;
